import React, { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import { t, tr } from "../../i18n/translations";

// Fallback seed (used only if backend has no services configured yet)
const FALLBACK_SERVICES = [
  {
    id: "seed-bridal",
    slug: "bridal",
    title_es: "Maquillaje para Novias", title_en: "Bridal Makeup", title_jp: "ブライダルメイク",
    description_es: "Una experiencia íntima diseñada para tu día más importante.",
    description_en: "An intimate experience crafted for your most important day.",
    description_jp: "あなたの最も大切な一日のために作られた特別な体験。",
    price_from: 6500,
    image_url: "https://images.pexels.com/photos/16096141/pexels-photo-16096141.png",
  },
  {
    id: "seed-editorial",
    slug: "editorial",
    title_es: "Maquillaje Editorial", title_en: "Editorial Makeup", title_jp: "エディトリアルメイク",
    description_es: "Construcción de personajes para campañas y editoriales.",
    description_en: "Character and concept building for campaigns and editorials.",
    description_jp: "キャンペーンとエディトリアルのためのキャラクター作り。",
    price_from: 4500,
    image_url: "https://images.unsplash.com/photo-1557924760-b14a6d4a4ef3?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    id: "seed-events",
    slug: "events",
    title_es: "Eventos de Belleza", title_en: "Beauty Events", title_jp: "ビューティイベント",
    description_es: "Coberturas de activaciones y eventos del sector belleza.",
    description_en: "Coverage for brand activations and beauty industry events.",
    description_jp: "ブランドイベントのサポート。",
    price_from: 3800,
    image_url: "https://images.unsplash.com/photo-1613552465135-e5bdaada10bc?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    id: "seed-social",
    slug: "social",
    title_es: "Eventos Sociales", title_en: "Social Events", title_jp: "ソーシャルイベント",
    description_es: "XV años, graduaciones y celebraciones especiales.",
    description_en: "Quinceañera, graduations and special celebrations.",
    description_jp: "特別な記念日のためのメイク。",
    price_from: 2200,
    image_url: "https://images.pexels.com/photos/8221202/pexels-photo-8221202.jpeg",
  },
];

// ─── Deliverables por slug (fallback si el backend no lo trae) ──
// Cada servicio muestra 4-5 puntos de valor tangible.
const DELIVERABLES_BY_SLUG = {
  bridal: [
    "Consulta previa 45 min",
    "Prueba con documentación",
    "Kit de retoque exclusivo",
    "WhatsApp directo el día D",
    "Skin prep timeline personal",
  ],
  novia: [
    "Consulta previa 45 min",
    "Prueba con documentación",
    "Kit de retoque exclusivo",
    "WhatsApp directo el día D",
    "Skin prep timeline personal",
  ],
  novias: [
    "Consulta previa 45 min",
    "Prueba con documentación",
    "Kit de retoque exclusivo",
    "WhatsApp directo el día D",
    "Skin prep timeline personal",
  ],
  editorial: [
    "Lectura del brief creativo",
    "Referencias visuales aprobadas",
    "Acompañamiento en set",
    "Retoque entre tomas",
    "Kit de emergencia profesional",
  ],
  events: [
    "Cobertura durante el evento",
    "Retoque en vivo",
    "Setup portátil profesional",
    "Servicio a domicilio",
    "Coordinación con producción",
  ],
  eventos: [
    "Cobertura durante el evento",
    "Retoque en vivo",
    "Setup portátil profesional",
    "Servicio a domicilio",
    "Coordinación con producción",
  ],
  social: [
    "Look diseñado a medida",
    "Fijación de larga duración",
    "Ajuste al vestuario",
    "Kit de retoque incluido",
    "Prueba opcional disponible",
  ],
  sociales: [
    "Look diseñado a medida",
    "Fijación de larga duración",
    "Ajuste al vestuario",
    "Kit de retoque incluido",
    "Prueba opcional disponible",
  ],
};

const DEFAULT_DELIVERABLES = [
  "Consulta personalizada",
  "Productos profesionales",
  "Skin prep incluido",
  "Retoque final",
];

export default function Services({ onBook }) {
  const { lang } = useLang();
  const [services, setServices] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    api
      .get("/services")
      .then((r) => setServices(r.data || []))
      .catch(() => setServices([]))
      .finally(() => setLoaded(true));
  }, []);

  const list = loaded && services.length === 0 ? FALLBACK_SERVICES : services;

  return (
    <section
      id="services"
      className="bg-[#FAFAFA] py-24 sm:py-32"
      data-testid="services-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        {/* ── Header ── */}
        <div className="mb-16 sm:mb-20">
          <p className="overline text-[#8B6D5E] mb-4">{tr(t.services.overline, lang)}</p>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1] max-w-3xl">
            {tr(t.services.title, lang)}
          </h2>
        </div>

        {/* ── Rows (OddlyMade sticky-stack pattern) ── */}
        <div className="border-t border-[#1A1A1A]/15" data-testid="services-list">
          {list.map((s, i) => {
            const title = s[`title_${lang}`] || s.title_es;
            const desc = s[`description_${lang}`] || s.description_es;
            const deliverables =
              (s.deliverables && s.deliverables.length ? s.deliverables : null) ||
              DELIVERABLES_BY_SLUG[s.slug] ||
              DEFAULT_DELIVERABLES;
            const isHovered = hovered === i;
            return (
              <div
                key={s.id}
                data-testid={`service-row-${s.slug}`}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onBook(s.slug)}
                className="group sticky top-20 sm:top-24 bg-[#FAFAFA] border-b border-[#1A1A1A]/15 cursor-pointer transition-colors"
                style={{ zIndex: 10 + i }}
              >
                <div className="grid grid-cols-12 gap-4 sm:gap-6 lg:gap-8 items-start py-8 sm:py-10 lg:py-14">
                  {/* Image */}
                  <div className="col-span-4 sm:col-span-3 lg:col-span-2">
                    <div
                      className={`relative aspect-[4/5] overflow-hidden bg-[#F4F0EC] transition-all duration-700 ${
                        isHovered ? "scale-[1.02]" : "scale-100"
                      }`}
                    >
                      <img
                        src={s.image_url}
                        alt={title}
                        loading="lazy"
                        className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out ${
                          isHovered ? "scale-[1.10]" : "scale-100"
                        }`}
                      />
                    </div>
                  </div>

                  {/* Number + title + desc */}
                  <div className="col-span-8 sm:col-span-9 lg:col-span-6 flex flex-col justify-between min-h-[100px] sm:min-h-[140px] lg:min-h-[180px]">
                    <div className="flex items-baseline gap-3 sm:gap-6">
                      <span
                        className="overline text-[#8B6D5E] tabular-nums flex-none"
                        data-testid={`service-num-${s.slug}`}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <h3
                        className={`font-serif text-3xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#1A1A1A] transition-transform duration-500 ${
                          isHovered ? "translate-x-2" : "translate-x-0"
                        }`}
                        data-testid={`service-title-${s.slug}`}
                      >
                        {title}
                        <span className="text-[#8B6D5E]">.</span>
                      </h3>
                    </div>
                    <div className="mt-4 lg:mt-0 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-6">
                      <p className="text-[#5C5C5C] leading-relaxed text-sm sm:text-base max-w-md hidden sm:block">
                        {desc}
                      </p>
                      <p className="overline text-[#1A1A1A] flex-none">
                        {tr(t.services.from, lang)} ${s.price_from.toLocaleString("es-MX")}{" "}
                        {tr(t.services.mxn, lang)}
                      </p>
                    </div>
                  </div>

                  {/* Deliverables list */}
                  <div
                    className="col-span-12 lg:col-span-4 lg:pl-6 lg:border-l lg:border-[#1A1A1A]/15"
                    data-testid={`service-deliverables-${s.slug}`}
                  >
                    <ul className="divide-y divide-[#1A1A1A]/10">
                      {deliverables.slice(0, 5).map((d, k) => (
                        <li
                          key={k}
                          className="flex items-baseline gap-3 py-2.5 text-[#1A1A1A] text-sm sm:text-[15px]"
                        >
                          <span className="overline text-[#8B6D5E] text-[10px] flex-none w-6">
                            {String(k + 1).padStart(2, "0")}
                          </span>
                          <span className="leading-snug">{d}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onBook(s.slug);
                      }}
                      className={`mt-5 overline text-[#1A1A1A] flex items-center gap-2 group/btn transition-colors hover:text-[#8B6D5E]`}
                      data-testid={`service-request-${s.slug}`}
                    >
                      {tr(t.services.request, lang)}
                      <ArrowRight
                        size={14}
                        className={`transition-transform duration-500 ${
                          isHovered ? "translate-x-2" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .font-serif { font-family: 'Cormorant Garamond', 'Times New Roman', serif; }
        .overline { font-family: 'Outfit', sans-serif; text-transform: uppercase; letter-spacing: .24em; font-size: 11px; font-weight: 600; }
      `}</style>
    </section>
  );
}
